import { useMemo } from 'react';

import { useFlags } from '@mint-lib/routing-context';
import { SlotPortal } from '@mint-lib/slots';

import { useTags } from '../../api/queries/tags.js';
import { useUsersTags } from '../../api/queries/usersTags.js';
import { UserType } from '../../api/users/index.js';
import { useModal } from '../../context/ModalContext/useModal.js';
import Filters from '../../features/Filters/Filters.jsx';
import UserTable from '../../features/UserTable/index.js';
import UserTableV2 from '../../features/UserTableV2/index.js';
import useIsReadOnlyPermission from '../../hooks/useIsReadOnlyPermission/useIsReadOnlyPermission.js';
import useTablePagination from '../../hooks/useTablePagination/useTablePagination.jsx';
import UserModal from '../../organisms/UserModal/index.jsx';
import { DEFAULT_PAGINATION_STATE } from '../../store/atoms/TablePagination/constants.js';
import useUserPageActions from './useUserPageActions.js';

const UserPage = () => {
  const ff = useFlags('@mint/arm', [
    'brandsAndGeoToMdm',
    'tagsForUserModalView',
    'usersManagementAgGrid',
  ]);

  const isAgGridEnabled = ff.usersManagementAgGrid.enabled;
  const isReadonlyPermission = useIsReadOnlyPermission();

  const { open: isNewModalOpened, setOpen: setIsNewModalOpened } = useModal();

  const tagsData = useTags(ff.brandsAndGeoToMdm.enabled);

  // Labels load separately from the rows, so resolve them here rather than at
  // click time: the modal then updates itself when they arrive.
  const { userTagsById, isUserTagsLoading } = useUsersTags(isAgGridEnabled);

  const {
    users,
    editTarget,
    setEditTarget,
    isUserExistInWorkspace,
    setIsUserExistInWorkspace,
    userTableV2Ref,
    handleCreate,
    handleClose,
    handleEditClose,
    handleEditUser,
  } = useUserPageActions({
    tags: tagsData.tags,
    isAgGridEnabled,
    setIsNewModalOpened,
  });

  // init pagination
  useTablePagination({
    ...DEFAULT_PAGINATION_STATE,
    totalItems: users?.data?.count ?? 0,
  });

  const editTargetWithTags = useMemo(() => {
    if (!editTarget) {
      return undefined;
    }

    return {
      ...editTarget,
      tags: userTagsById.get(Number(editTarget.id)) ?? [],
    };
  }, [editTarget, userTagsById]);

  const noResultsStatus =
    !users.isLoading && users?.data?.results?.length === 0;

  const notNullUserData = (users?.data?.results || []) as UserType[];

  return (
    <>
      {isAgGridEnabled ? (
        <UserTableV2
          ref={userTableV2Ref}
          isReadonlyPermission={isReadonlyPermission}
          onEditClick={setEditTarget}
          tabs={tagsData.tabs}
        />
      ) : (
        <UserTable
          data={notNullUserData}
          noResultsStatus={noResultsStatus}
          dataIsLoading={users.isPlaceholderData || users.isLoading}
          isReadonlyPermission={isReadonlyPermission}
          tabs={tagsData.tabs}
        />
      )}
      <SlotPortal name="filters">
        <Filters key={'user'} />
      </SlotPortal>

      {isNewModalOpened ? (
        <UserModal
          onClose={handleClose}
          onSubmit={handleCreate}
          isActive={isNewModalOpened}
          isReadonlyPermission={isReadonlyPermission}
          isUserExistInWorkspace={isUserExistInWorkspace}
          setIsUserExistInWorkspace={setIsUserExistInWorkspace}
        />
      ) : null}

      {editTarget ? (
        <UserModal
          onClose={handleEditClose}
          onSubmit={handleEditUser}
          initialValues={editTargetWithTags}
          isTagsLoading={isUserTagsLoading}
          isActive={!!editTarget}
          isReadonlyPermission={isReadonlyPermission}
          initialView={
            ff.tagsForUserModalView.enabled
              ? 'editUserView'
              : 'existingUserView'
          }
        />
      ) : null}
    </>
  );
};

export default UserPage;
